import {useMutation, useQuery} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {toast} from '@ui/toast/toast';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';

export interface FamilyPlanMemberItem {
  id: number;
  member_email: string;
  member_user_id: number | null;
  created_at: string;
}

export interface FamilyPlanInfoResponse {
  eligible: boolean;
  is_owner: boolean;
  plan: {
    id: number;
    name: string;
    max_members: number;
    max_dependents: number;
  } | null;
  subscription: {
    id: number;
    gateway_status: string;
    renews_at: string | null;
    ends_at: string | null;
  } | null;
  members: FamilyPlanMemberItem[];
  available_slots: number;
}

const queryKey = ['billing', 'family-members'];

export function useFamilyPlanMembers() {
  return useQuery({
    queryKey,
    queryFn: () => fetchFamilyPlanInfo(),
    staleTime: 60 * 1000,
  });
}

function fetchFamilyPlanInfo(): Promise<FamilyPlanInfoResponse> {
  return apiClient.get('billing/family-members').then(r => r.data);
}

export function useAddFamilyMember() {
  const {trans} = useTrans();
  return useMutation({
    mutationFn: (payload: {email: string}) =>
      apiClient.post('billing/family-members', payload).then(r => r.data),
    onSuccess: response => {
      toast(trans(message('Member added successfully')));
      queryClient.invalidateQueries({queryKey});
    },
    onError: err => {
      showHttpErrorToast(err);
    },
  });
}

export function useUpdateFamilyMember() {
  const {trans} = useTrans();
  return useMutation({
    mutationFn: ({id, email}: {id: number; email: string}) =>
      apiClient.put(`billing/family-members/${id}`, {email}).then(r => r.data),
    onSuccess: () => {
      toast(trans(message('Member updated successfully')));
      queryClient.invalidateQueries({queryKey});
    },
    onError: err => {
      showHttpErrorToast(err);
    },
  });
}

export function useRemoveFamilyMember() {
  const {trans} = useTrans();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(`billing/family-members/${id}`).then(r => r.data),
    onSuccess: () => {
      toast(trans(message('Member removed successfully')));
      queryClient.invalidateQueries({queryKey});
    },
    onError: err => {
      showHttpErrorToast(err);
    },
  });
}
